import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Form,
  Button,
  Dropdown,
  Checkbox,
  Message,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../../helpers';

const BillingConfigModal = ({ open, onClose, config, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!config?.id;

  const [form, setForm] = useState({
    name: '',
    provider_type: 0,
    access_key: '',
    secret_key: '',
    extra_config: '',
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [providerOptions, setProviderOptions] = useState([]);

  useEffect(() => {
    API.get('/api/admin/channel-billing/supported-providers').then((res) => {
      if (res.data.success) {
        const opts = res.data.data.map((p) => ({
          key: p.type,
          text: p.name,
          value: p.type,
          importOnly: p.import_only === true,
        }));
        setProviderOptions(opts);
      }
    });
  }, []);

  useEffect(() => {
    if (config) {
      setForm({
        name: config.name || '',
        provider_type: config.provider_type,
        access_key: config.access_key || '',
        secret_key: '',
        extra_config: config.extra_config || '',
        enabled: config.enabled !== false,
      });
    } else {
      setForm((f) => ({
        name: '',
        provider_type:
          providerOptions.length > 0 ? providerOptions[0].value : 0,
        access_key: '',
        secret_key: '',
        extra_config: '',
        enabled: true,
      }));
    }
  }, [config, open, providerOptions]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await API.put(
          `/api/admin/channel-billing/configs/${config.id}`,
          form
        );
      } else {
        res = await API.post('/api/admin/channel-billing/configs', form);
      }
      if (res.data.success) {
        showSuccess(
          isEdit
            ? t('channel_billing.config.updated', '更新成功')
            : t('channel_billing.config.created', '创建成功')
        );
        onSaved();
        onClose();
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError(t('channel_billing.config.save_error', '保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const selectedProvider = providerOptions.find(
    (provider) => provider.value === form.provider_type
  );
  const isImportOnly = selectedProvider?.importOnly === true;

  return (
    <Modal open={open} onClose={onClose} size='small'>
      <Modal.Header>
        {isEdit
          ? t('channel_billing.config.edit_title', '编辑账单配置')
          : t('channel_billing.config.create_title', '新增账单配置')}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field required>
            <label>{t('channel_billing.config.name', '配置名称')}</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t(
                'channel_billing.config.name_placeholder',
                '例如：百度主账号'
              )}
            />
          </Form.Field>
          <Form.Field required>
            <label>{t('channel_billing.config.provider', '提供商')}</label>
            <Dropdown
              selection
              options={providerOptions}
              value={form.provider_type}
              onChange={(e, { value }) =>
                setForm({ ...form, provider_type: value })
              }
            />
          </Form.Field>
          {isImportOnly ? (
            <Message info>
              该提供商通过 Excel 导入账单，不需要 API 密钥。
            </Message>
          ) : (
            <>
              <Form.Field required>
                <label>Access Key</label>
                <input
                  value={form.access_key}
                  onChange={(e) =>
                    setForm({ ...form, access_key: e.target.value })
                  }
                />
              </Form.Field>
              <Form.Field required={!isEdit}>
                <label>
                  Secret Key{' '}
                  {isEdit &&
                    `(${t('channel_billing.config.sk_hint', '留空不修改')})`}
                </label>
                <input
                  type='password'
                  value={form.secret_key}
                  onChange={(e) =>
                    setForm({ ...form, secret_key: e.target.value })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>
                  {t('channel_billing.config.extra', '额外配置 (JSON)')}
                </label>
                <textarea
                  rows={3}
                  value={form.extra_config}
                  onChange={(e) =>
                    setForm({ ...form, extra_config: e.target.value })
                  }
                  placeholder='{"service_id": ["svcp-xxx"], "app_id": ["xxx"]}'
                />
              </Form.Field>
            </>
          )}
          <Form.Field>
            <Checkbox
              toggle
              label={t('channel_billing.config.enabled', '启用')}
              checked={form.enabled}
              onChange={(e, { checked }) =>
                setForm({ ...form, enabled: checked })
              }
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          {t('channel_billing.config.cancel', '取消')}
        </Button>
        <Button primary loading={saving} onClick={handleSubmit}>
          {t('channel_billing.config.save', '保存')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default BillingConfigModal;
